// Every English string on the kitchen-flow screens, in one place so an Urdu
// table can be dropped in later. Plain words: many readers are low-literacy.
import type { RequisitionKind, RequisitionStatus } from './types'

export const T = {
  productionTitle: 'Kitchen Production',
  productionSubtitle: 'Ask the store for stock, check what arrived, send leftovers back',
  notEnabled: 'This screen is not switched on for this restaurant.',
  settingsUnreachable: 'Could not check this screen — the connection may be down.',
  setupUnfinished: "Kitchen flow is switched on but its setup didn't finish — restart the backend or contact support.",
  tabRequest: 'Ask for stock',
  tabMine: 'My requests',
  tabReturn: 'Send back to store',
  tabRequests: 'Requests',
  tabLocations: 'Locations',
  search: 'Search items…',
  noItems: 'No items match.',
  add: 'Add',
  inList: 'In list',
  yourList: 'Your list',
  emptyList: 'Tap an item to add it.',
  neededBy: 'Needed by (optional)',
  note: 'Note (optional)',
  sendRequest: 'Send to store',
  sendReturn: 'Send back to store',
  sending: 'Sending…',
  sentOk: 'Sent to the store',
  returnSentOk: 'Return sent — the store will check it',
  queued: 'No connection — saved on this tablet, will send by itself',
  notSynced: (n: number) => `${n} not sent yet`,
  waitingToSend: 'Waiting to send — saved on this tablet',
  retryNow: 'Try again now',
  wholeNumbersOnly: 'Whole numbers only — rounded.',
  storeHas: 'Store has',
  kitchenHas: 'Kitchen has',
  asked: 'Asked',
  askedBy: 'asked by',
  approved: 'Approved',
  sent: 'Sent',
  got: 'Got',
  missing: 'Missing',
  missingNeedsManager: 'Missing — needs a manager',
  toSend: 'Still to send',
  receive: 'Check what arrived',
  receiveSave: 'Save what arrived',
  receiveOk: 'Saved',
  whyLess: 'Why less? (required)',
  shortBy: (qty: string, unit: string) => `${qty} ${unit} less than was sent:`,
  restComing: 'The rest is still coming',
  restMissing: 'It is missing',
  restComingHelp: 'It stays on its way — check it in when it arrives.',
  foundOpen: 'Missing stock turned up?',
  foundTitle: 'Missing stock turned up',
  foundHelp: 'Only use this if some of the missing stock was found. Enter how much arrived.',
  approve: 'Approve',
  approveOk: 'Approved',
  reject: 'Reject all',
  rejectReason: 'Why are you rejecting it? (required)',
  rejectOk: 'Rejected',
  issue: 'Send stock now',
  issueOk: 'Stock sent to the kitchen',
  closeShort: 'Cannot send the rest',
  closeShortReason: 'Why? (e.g. not in stock)',
  cancel: 'Cancel request',
  cancelOk: 'Request cancelled',
  submitDraft: 'Send to store',
  writeOff: 'Write off missing',
  writeOffPin: 'Manager PIN',
  writeOffOk: 'Written off',
  useFirst: (when: string) => `Use the batch expiring ${when} first`,
  expiredThrowAway: (qty: string, unit: string, when: string) => `${qty} ${unit} expired on ${when} — throw it away`,
  expiredThrowAwayRange: (qty: string, unit: string, from: string, to: string) =>
    `${qty} ${unit} expired between ${from} and ${to} — throw it away`,
  storeHasInDate: (qty: string, unit: string) => `The store has only ${qty} ${unit} that is in date`,
  buyList: 'What to buy',
  nothingToBuy: 'The store can cover everything asked for.',
  noSupplier: 'No supplier set',
  noSupplierHint: 'Buy these at the market and record them in Purchasing → New purchase → Already bought & paid, or set a supplier on the item to order them from here.',
  timeline: 'What happened',
  by: 'by',
  pinBy: 'PIN',
  fill: 'Filled',
  empty: 'Nothing here yet.',
  live: 'Live',
  offline: 'Offline',
  reconnecting: 'Reconnecting…',
  from: 'From',
  to: 'To',
  close: 'Close',
  loading: 'Loading…',
  locationsTitle: 'Locations',
  locationsHelp:
    'Where stock is kept. Stock moves between them only through a request. In Transit is used by the system and cannot be changed.',
  addLocation: 'Add location',
  locationName: 'Name',
  locationKind: 'Kind',
  receivesDeliveries: 'Receives deliveries',
  salesDrawFrom: 'Sales draw from here',
  makeReceiving: 'Make this receive deliveries',
  makeSaleSource: 'Make sales draw from here',
  switchOff: 'Switch off',
  switchOn: 'Switch on',
  holdsStock: (n: number) => `Holds ${n} item${n === 1 ? '' : 's'}`,
  saved: 'Saved',
  // Errors (friendlyError.ts)
  errOffline: 'No connection. Try again.',
  errOfflineQueued: "No connection — it'll be sent when the tablet is back online",
  errServer: 'Something went wrong on our side. Try again.',
  errGeneric: 'Something went wrong. Try again.',
  // The tablet's queue (offlineQueue.ts)
  queueFailedTitle: "Couldn't send",
  queueFailedHelp: 'Tap Try again, or Discard if it is no longer needed.',
  queueHeldBy: (name: string) => `Saved by ${name} on this tablet`,
  queueHeldSomeone: 'Saved by someone else on this tablet',
  queueHeldHelp: 'It will be sent when they sign in on this tablet. Discard it if it is no longer needed.',
  queueTryAgain: 'Try again',
  queueDiscard: 'Discard',
  queueDiscarded: 'Discarded',
  queueGaveUp: (label: string) => `Couldn't send: ${label}`,
  // Ask for stock (RequestBuilder)
  loadFailed: 'Could not load this — the connection may be down.',
  retry: 'Try again',
  showingOf: (shown: number, total: number) => `Showing ${shown} of ${total} — search to find more`,
  showMore: 'Show more',
  restoredList: 'Restored your unsent list',
  scanAddedAsk: (name: string, qty: string, unit: string) => `${name}: ${qty} ${unit} on your list`,
  scanNotHeld: (name: string) => `The kitchen has no ${name} to send back`,
  scanUnknownItem: (name: string) => `${name} is not a stock item here`,
} as const

export function kindLabel(kind: RequisitionKind): string {
  return kind === 'return' ? 'Return to store' : 'Stock request'
}

export interface StatusMeta {
  label: string
  /** Tailwind classes for the chip. */
  cls: string
}

const NEUTRAL = 'bg-muted text-muted-foreground'
const WAIT = 'bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200'
const MOVE = 'bg-blue-100 text-blue-900 dark:bg-blue-950/50 dark:text-blue-200'
const GOOD = 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-200'
const BAD = 'bg-red-100 text-red-900 dark:bg-red-950/50 dark:text-red-200'

export function statusMeta(status: RequisitionStatus, kind: RequisitionKind = 'issue'): StatusMeta {
  switch (status) {
    case 'draft':
      return { label: 'Not sent', cls: NEUTRAL }
    case 'submitted':
      return { label: 'Waiting for store', cls: WAIT }
    case 'approved':
      return { label: 'Approved', cls: WAIT }
    case 'partially_issued':
      return { label: 'Part sent', cls: MOVE }
    case 'issued':
      return { label: kind === 'return' ? 'On the way to store' : 'On the way', cls: MOVE }
    case 'received':
      return { label: 'Arrived — some missing', cls: BAD }
    case 'closed':
      return { label: 'Done', cls: GOOD }
    case 'rejected':
      return { label: 'Rejected', cls: BAD }
    case 'cancelled':
      return { label: 'Cancelled', cls: NEUTRAL }
  }
}

const EVENT_LABELS: Record<string, string> = {
  created: 'Request written',
  edited: 'Request changed',
  submitted: 'Sent to store',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  partially_issued: 'Part sent',
  issued: 'All sent',
  received: 'Arrived — some missing',
  closed: 'Done',
  line_approved: 'Approved amount changed',
  line_issued: 'Sent',
  line_closed_short: 'Rest will not be sent',
  line_received: 'Received',
  line_received_short: 'Received less than sent',
  line_written_off: 'Missing stock written off',
}

export function eventLabel(type: string): string {
  return EVENT_LABELS[type] ?? type.replace(/_/g, ' ')
}
